/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

/**
 * Merges the rest of data with header data
 * Criteria: Header data needs to have same key length and keys in its array and restData needs to have keys with length
 * equal to or more than that of headersData
 * Adds empty headers if restData has more keys than headers
 * @param headerData
 * @param restData
 * @returns
 */
export const compileHeadersAndDataForCSV = (
  headerData: Record<string, string | number>[],
  restData: Record<string, string>[],
) => {
  if (!restData?.length) return headerData;
  const dataKeyLength = Object.keys(restData?.[0]).length;

  const headerKeyLength = Object.keys(headerData?.[0]).length || 0;
  if (dataKeyLength === headerKeyLength) return [...headerData, ...restData];
  const modifiedHeaders = [];
  for (const header of headerData) {
    let modifiedHeader = header;
    let emptyHeader = "";
    for (let i = headerKeyLength; i < dataKeyLength; i++) {
      modifiedHeader = {
        ...modifiedHeader,
        [(emptyHeader += " ")]: "", //empty Headers for extra data on data (increase whitespace to make key equal)
      };
    }
    modifiedHeaders.push(modifiedHeader);
  }
  return [...modifiedHeaders, ...restData];
};

/**
 * Modify data with header data to fit in csv format
 * Labels of Headers mapped with values of data and add empty headers for rest or empty values for rest
 * @param headerData
 * @param data
 * @returns
 */
export const mapDataValuesToHeaderKeys = (headerData: Record<string, string>, data: Record<string, string>) => {
  let emptyHeader = "";

  const headerKeys = Object.keys(headerData);
  const dataKeys = Object.keys(data);
  const newData: Record<string, string> = dataKeys?.reduce((newData, firstDataKey, index) => {
    let key;
    if (!headerKeys[index]) key = emptyHeader += " ";
    else key = headerKeys[index];
    return {
      ...newData,
      [key]: Object.values(data)[index],
    };
  }, {});
  const newDataKeys = Object.keys(newData);
  if (dataKeys.length < headerKeys.length) {
    for (let i = newDataKeys.length; i < headerKeys.length; i++) {
      newData[headerKeys[i]] = "";
    }
  }
  return newData;
};

/**
 * Create Empty row by mapping header keys with empty values
 * @param headerData
 * @returns
 */
export const createEmptyRow = (headerData: Record<string, string>) =>
  Object.fromEntries(Object.entries(headerData).map(([key, val]) => [key, ""]));
